import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSearchParams } from 'react-router-dom';
import { reservationFormsApi, ReservationForm } from '../services/reservationFormsService.ts';

const ReservationFormPage: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { sessionToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const reservationFormId = searchParams.get('id');

  // Form state
  const [formData, setFormData] = useState({
    status: 'Pending',
    contactName: '',
    phone: '',
    email: '',
    reservationDate: '',
    arrivalTime: '',
    arrivalTimePeriod: 'AM',
    adults: '',
    children: '',
    highchairs: '',
    tablePreference: [] as string[],
    occasion: '',
    occasionOther: '',
    allergies: '',
    specialRequests: '',
    assignedTable: '',
    staffInitials: '',
  });

  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Table preference options
  const tablePreferenceOptions = ['Indoor Dining', 'Outdoor/Patio', 'Bar Seating', 'Private Room'];

  // Occasion options
  const occasionOptions = ['Birthday', 'Anniversary', 'Business', 'other'];

  // Fetch reservation form data if editing
  useEffect(() => {
    const fetchReservationForm = async () => {
      if (!reservationFormId || !sessionToken) return;

      setLoading(true);
      try {
        const data = await reservationFormsApi.getReservationForm(parseInt(reservationFormId), sessionToken);
        
        // Parse guests (JSON string to object)
        let guestsData = { adults: '', children: '', highchairs: '' };
        if (data.guests) {
          try {
            guestsData = JSON.parse(data.guests);
          } catch (e) {
            console.error('Error parsing guests:', e);
          }
        }

        // Parse table preference (JSON string to array)
        let tablePrefs: string[] = [];
        if (data.tablePreference) {
          try {
            tablePrefs = JSON.parse(data.tablePreference);
          } catch (e) {
            console.error('Error parsing table preference:', e);
          }
        }

        // Parse arrival time (format: "10:00 AM" or "19:00")
        let arrivalTime = '';
        let arrivalTimePeriod = 'AM';
        if (data.arrivalTime) {
          // Check if it's in 24-hour format
          const time24Match = data.arrivalTime.match(/^(\d{1,2}):(\d{2})$/);
          if (time24Match) {
            const hours = parseInt(time24Match[1]);
            const minutes = time24Match[2];
            if (hours === 0) {
              arrivalTime = '12';
              arrivalTimePeriod = 'AM';
            } else if (hours < 12) {
              arrivalTime = `${hours}:${minutes}`;
              arrivalTimePeriod = 'AM';
            } else if (hours === 12) {
              arrivalTime = '12';
              arrivalTimePeriod = 'PM';
            } else {
              arrivalTime = `${hours - 12}:${minutes}`;
              arrivalTimePeriod = 'PM';
            }
          } else {
            // Already in 12-hour format
            const time12Match = data.arrivalTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
            if (time12Match) {
              arrivalTime = `${time12Match[1]}:${time12Match[2]}`;
              arrivalTimePeriod = time12Match[3].toUpperCase();
            } else {
              arrivalTime = data.arrivalTime;
            }
          }
        }

        // Determine if occasion is "other" or a standard option
        const occasionValue = data.occasion || '';
        const isOtherOccasion = !occasionOptions.slice(0, -1).includes(occasionValue);
        
        setFormData({
          status: data.status || 'Pending',
          contactName: data.contactName || '',
          phone: data.phone || '',
          email: data.email || '',
          reservationDate: data.reservationDate || '',
          arrivalTime,
          arrivalTimePeriod,
          adults: guestsData.adults?.toString() || '',
          children: guestsData.children?.toString() || '',
          highchairs: guestsData.highchairs?.toString() || '',
          tablePreference: tablePrefs,
          occasion: isOtherOccasion ? 'other' : occasionValue,
          occasionOther: isOtherOccasion ? occasionValue : '',
          allergies: data.allergies || '',
          specialRequests: data.specialRequests || '',
          assignedTable: data.assignedTable || '',
          staffInitials: data.staffInitials || '',
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch reservation form';
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (reservationFormId) {
      fetchReservationForm();
    }
  }, [reservationFormId, sessionToken]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!sessionToken) return;

    if (!formData.contactName) {
      showError('Primary Contact Name is required');
      return;
    }

    if (!formData.reservationDate) {
      showError('Date of reservation is required');
      return;
    }

    if (!formData.arrivalTime) {
      showError('Time of Arrival is required');
      return;
    }

    setSaving(true);
    try {
      // Combine arrival time and period
      const arrivalTimeCombined = `${formData.arrivalTime} ${formData.arrivalTimePeriod}`;

      // Prepare guests JSON
      const guestsData = {
        adults: formData.adults ? parseInt(formData.adults) || 0 : 0,
        children: formData.children ? parseInt(formData.children) || 0 : 0,
        highchairs: formData.highchairs ? parseInt(formData.highchairs) || 0 : 0,
      };
      const guestsJson = JSON.stringify(guestsData);

      // Prepare table preference JSON
      const tablePreferenceJson = formData.tablePreference.length > 0 
        ? JSON.stringify(formData.tablePreference) 
        : null;

      // Determine occasion value
      const occasionValue = formData.occasion === 'other' 
        ? formData.occasionOther 
        : formData.occasion;

      // Prepare data for API
      const apiData = {
        contactName: formData.contactName,
        phone: formData.phone || null,
        email: formData.email || null,
        reservationDate: formData.reservationDate,
        arrivalTime: arrivalTimeCombined,
        guests: guestsJson,
        tablePreference: tablePreferenceJson,
        occasion: occasionValue || null,
        allergies: formData.allergies || null,
        specialRequests: formData.specialRequests || null,
        status: formData.status || null,
        assignedTable: formData.assignedTable || null,
        staffInitials: formData.staffInitials || null,
      };

      if (reservationFormId) {
        // Update existing
        await reservationFormsApi.updateReservationForm(parseInt(reservationFormId), apiData, sessionToken);
        showSuccess('Reservation form updated successfully!');
      } else {
        // Create new
        const createdForm = await reservationFormsApi.createReservationForm(apiData, sessionToken);
        showSuccess('Reservation form created successfully!');
        
        // Update URL to include the ID and fetch the form data
        setSearchParams({ id: createdForm.id.toString() });
        
        // Fetch the created form to load it into edit mode
        const data = await reservationFormsApi.getReservationForm(createdForm.id, sessionToken);
        
        // Parse guests
        let guestsData = { adults: '', children: '', highchairs: '' };
        if (data.guests) {
          try {
            const parsed = JSON.parse(data.guests);
            guestsData = {
              adults: parsed.adults?.toString() || '',
              children: parsed.children?.toString() || '',
              highchairs: parsed.highchairs?.toString() || '',
            };
          } catch (e) {
            console.error('Error parsing guests:', e);
          }
        }

        // Parse table preference
        let tablePrefs: string[] = [];
        if (data.tablePreference) {
          try {
            tablePrefs = JSON.parse(data.tablePreference);
          } catch (e) {
            console.error('Error parsing table preference:', e);
          }
        }

        // Parse arrival time
        let arrivalTime = '';
        let arrivalTimePeriod = 'AM';
        if (data.arrivalTime) {
          const time24Match = data.arrivalTime.match(/^(\d{1,2}):(\d{2})$/);
          if (time24Match) {
            const hours = parseInt(time24Match[1]);
            const minutes = time24Match[2];
            if (hours === 0) {
              arrivalTime = '12';
              arrivalTimePeriod = 'AM';
            } else if (hours < 12) {
              arrivalTime = `${hours}:${minutes}`;
              arrivalTimePeriod = 'AM';
            } else if (hours === 12) {
              arrivalTime = '12';
              arrivalTimePeriod = 'PM';
            } else {
              arrivalTime = `${hours - 12}:${minutes}`;
              arrivalTimePeriod = 'PM';
            }
          } else {
            const time12Match = data.arrivalTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
            if (time12Match) {
              arrivalTime = `${time12Match[1]}:${time12Match[2]}`;
              arrivalTimePeriod = time12Match[3].toUpperCase();
            } else {
              arrivalTime = data.arrivalTime;
            }
          }
        }

        // Load the form data
        // Determine if occasion is "other" or a standard option
        const occasionValue = data.occasion || '';
        const isOtherOccasion = !occasionOptions.slice(0, -1).includes(occasionValue);
        
        setFormData({
          status: data.status || 'Pending',
          contactName: data.contactName || '',
          phone: data.phone || '',
          email: data.email || '',
          reservationDate: data.reservationDate || '',
          arrivalTime,
          arrivalTimePeriod,
          adults: guestsData.adults,
          children: guestsData.children,
          highchairs: guestsData.highchairs,
          tablePreference: tablePrefs,
          occasion: isOtherOccasion ? 'other' : occasionValue,
          occasionOther: isOtherOccasion ? occasionValue : '',
          allergies: data.allergies || '',
          specialRequests: data.specialRequests || '',
          assignedTable: data.assignedTable || '',
          staffInitials: data.staffInitials || '',
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save reservation form';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Reservation Form
        </Typography>
      </Box>
      <Paper sx={{ p: 3 }}>
        {/* First Row: Status (left) and Save Button (right) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) => handleInputChange('status', e.target.value)}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Confirmed">Confirmed</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Second Row: Guest Information */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Guest Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Primary Contact Name"
                value={formData.contactName}
                onChange={(e) => handleInputChange('contactName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Third Row: Booking Details */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Booking Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Date of reservation"
                type="date"
                value={formData.reservationDate}
                onChange={(e) => handleInputChange('reservationDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Time of Arrival"
                  value={formData.arrivalTime}
                  onChange={(e) => handleInputChange('arrivalTime', e.target.value)}
                  placeholder="10:00"
                  required
                  sx={{ flex: 1 }}
                />
                <FormControl sx={{ minWidth: 100 }}>
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={formData.arrivalTimePeriod}
                    label="Period"
                    onChange={(e) => handleInputChange('arrivalTimePeriod', e.target.value)}
                  >
                    <MenuItem value="AM">AM</MenuItem>
                    <MenuItem value="PM">PM</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Number of Guests
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Adults"
                    type="number"
                    value={formData.adults}
                    onChange={(e) => handleInputChange('adults', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Children"
                    type="number"
                    value={formData.children}
                    onChange={(e) => handleInputChange('children', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Highchairs Needed"
                    type="number"
                    value={formData.highchairs}
                    onChange={(e) => handleInputChange('highchairs', e.target.value)}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* Fourth Row: Table Preference */}
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            multiple
            options={tablePreferenceOptions}
            value={formData.tablePreference}
            onChange={(event, newValue) => handleInputChange('tablePreference', newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Table Preference"
                placeholder="Select table preferences"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
          />
        </Box>

        {/* Fifth Row: Occasion & Special Requests */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Occasion & Special Requests
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Occasion</InputLabel>
                <Select
                  value={formData.occasion}
                  label="Occasion"
                  onChange={(e) => handleInputChange('occasion', e.target.value)}
                >
                  <MenuItem value="Birthday">Birthday</MenuItem>
                  <MenuItem value="Anniversary">Anniversary</MenuItem>
                  <MenuItem value="Business">Business</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.occasion === 'other' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Please specify"
                  value={formData.occasionOther}
                  onChange={(e) => handleInputChange('occasionOther', e.target.value)}
                  required
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Allergies/Dietary Restrictions"
                value={formData.allergies}
                onChange={(e) => handleInputChange('allergies', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Special Requests"
                value={formData.specialRequests}
                onChange={(e) => handleInputChange('specialRequests', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Sixth Row: Internal Use Only */}
        <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Internal Use Only
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Assigned Table"
                value={formData.assignedTable}
                onChange={(e) => handleInputChange('assignedTable', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Staff Initials"
                value={formData.staffInitials}
                onChange={(e) => handleInputChange('staffInitials', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>
      </Paper>
    </Box>
  );
};

export default ReservationFormPage;

